import {
  Activity,
  ChevronRight,
  Clock3,
  Database,
  Radio,
  RefreshCcw,
  Watch,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { CSSProperties } from "react";
import type {
  DelayAlert,
  LiveSnapshot,
  Recommendation,
  WorkerId,
  WorkerState,
} from "../types";
import { DelayHistoryPanel } from "./DelayHistoryPanel";
import { KitchenScene } from "./KitchenScene";
import { TaskSheet } from "./TaskSheet";

const stationRevealOrder = [
  "gas_range",
  "three_compartment_sink",
  "deep_fryer",
  "center_prep_table",
  "dishwasher",
  "convection_oven",
  "cold_prep_station",
  "microwave_station",
  "flat_top_griddle",
  "reach_in_fridge",
  "range_oven",
  "under_counter_fridge",
  "reach_in_freezer",
  "dry_storage_shelf",
  "dish_storage_shelf",
  "plate_storage_table",
  "trash_station",
] as const;

const delayProgressionMinutes = [1, 3, 5, 8, 11, 14, 17, 20, 22, 24, 26, 28, 30, 32, 34];

type DelayAlertStyle = CSSProperties & {
  "--delay-color": string;
  "--delay-soft": string;
};

function formatSeconds(total: number) {
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

export function LiveView({
  snapshot,
  mode,
  connected,
  gestureHint,
  registerBandPress,
  approve,
  pass,
  reset,
}: {
  snapshot: LiveSnapshot;
  mode: "idle" | "offered" | "active";
  connected: boolean;
  gestureHint: string;
  registerBandPress: () => void;
  approve: () => void;
  pass: () => void;
  reset: () => void;
}) {
  const recommendation = snapshot.activeRecommendation;
  const [selectedWorkerId, setSelectedWorkerId] = useState<WorkerId>("A");
  const [showDelayHistory, setShowDelayHistory] = useState(false);
  const [demoEventStep, setDemoEventStep] = useState(0);

  const orderedStations = useMemo(() => {
    const order = new Map(stationRevealOrder.map((stationId, index) => [stationId, index]));
    return [...snapshot.stations].sort(
      (left, right) =>
        (order.get(left.id as (typeof stationRevealOrder)[number]) ?? Number.MAX_SAFE_INTEGER) -
        (order.get(right.id as (typeof stationRevealOrder)[number]) ?? Number.MAX_SAFE_INTEGER),
    );
  }, [snapshot.stations]);
  const revealedStationCount = Math.min(2 + demoEventStep, orderedStations.length);
  const visibleStations = orderedStations.slice(0, revealedStationCount);
  const visibleStationIds = visibleStations.map((station) => station.id);

  const advanceDemoEvent = useCallback(() => {
    setDemoEventStep((current) =>
      Math.min(current + 1, Math.max(orderedStations.length - 2, 0)),
    );
  }, [orderedStations.length]);

  const stagedDelayAlert = useMemo<DelayAlert | null>(() => {
    if (demoEventStep === 0) return null;

    const source = snapshot.delayAlert;
    const progressionIndex = Math.min(
      demoEventStep - 1,
      delayProgressionMinutes.length - 1,
    );
    const predictedDelayMinutes = delayProgressionMinutes[progressionIndex];
    const predictedReadyInMinutes = 19 + predictedDelayMinutes;
    const evidenceCount = Math.min(
      Math.max(2, demoEventStep + 1),
      source?.evidenceCount ?? 8,
    );

    return {
      itemLabel: source?.itemLabel ?? "Jajangmyeon vegetable prep",
      stationLabel: source?.stationLabel ?? "Center Cutting Station",
      predictedReadyInMinutes,
      predictedDelayMinutes,
      similarity: Math.min(0.76 + demoEventStep * 0.025, 0.97),
      summary:
        predictedDelayMinutes < 8
          ? "Incoming station events are beginning to match a historical prep-to-wok slowdown."
          : predictedDelayMinutes < 17
            ? "The growing queue and current workforce now closely match previous delayed shifts."
            : "Multiple live events confirm a severe prep-to-wok delay pattern. Reassignment is recommended now.",
      evidenceCount,
      status: "active",
      updatedAt: source?.updatedAt,
    };
  }, [demoEventStep, snapshot.delayAlert]);

  const delayAlertStyle = useMemo<DelayAlertStyle | undefined>(() => {
    if (!stagedDelayAlert) return undefined;
    const severity = Math.min(stagedDelayAlert.predictedDelayMinutes / 20, 1);
    const hue = Math.round(36 * (1 - severity));
    return {
      "--delay-color": `hsl(${hue} 88% 46%)`,
      "--delay-soft": `hsl(${hue} 100% 95%)`,
    };
  }, [stagedDelayAlert]);

  useEffect(() => {
    if (recommendation) setSelectedWorkerId(recommendation.workerId);
  }, [recommendation]);

  useEffect(() => {
    const handleNextStation = (event: KeyboardEvent) => {
      if (event.key.toLowerCase() !== "n" || event.repeat) return;
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        return;
      }
      event.preventDefault();
      advanceDemoEvent();
    };

    window.addEventListener("keydown", handleNextStation);
    return () => window.removeEventListener("keydown", handleNextStation);
  }, [advanceDemoEvent]);

  const focusedWorker: WorkerState | undefined = snapshot.workers.find(
    (worker) => worker.id === selectedWorkerId,
  );
  const taskWorker: WorkerState | undefined = recommendation
    ? snapshot.workers.find((worker) => worker.id === recommendation.workerId)
    : focusedWorker;

  return (
    <main className="live-page">
      <section className="twin-stage" aria-label="Live overhead kitchen digital twin">
        <KitchenScene
          workers={snapshot.workers}
          stations={snapshot.stations}
          inventory={snapshot.inventory}
          recommendation={recommendation}
          selectedWorkerId={selectedWorkerId}
          visibleStationIds={visibleStationIds}
        />
        <div className="live-context">
          <span className="live-status">
            <i />
            LIVE {snapshot.clock}
          </span>
          <span>{connected ? "Firebase live sync" : "Local demo - reconnecting"}</span>
        </div>
        <div className="headline-metrics" aria-label="Key operating metrics">
          <div>
            <strong>{snapshot.orders}</strong>
            <span>Open tickets</span>
          </div>
          <div>
            <strong>{snapshot.averageEta}</strong>
            <span>Average ETA</span>
          </div>
          <div>
            <strong>{snapshot.synchronized}%</strong>
            <span>Table sync</span>
          </div>
        </div>
        <label className="worker-selector">
          <span>Focus worker</span>
          <select
            value={selectedWorkerId}
            onChange={(event) => setSelectedWorkerId(event.target.value as WorkerId)}
            aria-label="Select worker to focus"
          >
            {snapshot.workers.map((worker) => (
              <option key={worker.id} value={worker.id}>
                Worker {worker.id} - {worker.name}
              </option>
            ))}
          </select>
        </label>
        <div className="camera-note">
          <Radio size={15} />
          Camera perception - POS - inventory events
        </div>
        <div className="aisle-legend" aria-label="Aisle overlay legend">
          <strong>Aisle layer</strong>
          <span><i className="clear" /> pale blue floor · verified walkable path</span>
          <span><i className="route" /> blue dashes · selected worker route</span>
          <span><i className="target" /> outlined zone · action destination</span>
        </div>
        <button
          className="reset-scene"
          onClick={() => {
            setDemoEventStep(0);
            setShowDelayHistory(false);
            reset();
          }}
          type="button"
          aria-label="Reset demo"
        >
          <RefreshCcw size={17} />
        </button>
        <div className={`band-simulator ${mode}`}>
          <button type="button" onClick={registerBandPress} aria-label="Press Worker A wrist band">
            <Watch size={23} />
            <span>Worker A band</span>
          </button>
          <div>
            <strong>{gestureHint}</strong>
            <span>The device resolves presses within a 420 ms gesture window.</span>
          </div>
        </div>
      </section>

      <aside className="operations-rail" aria-label="Cooking section status">
        {showDelayHistory && stagedDelayAlert ? (
          <DelayHistoryPanel
            alert={stagedDelayAlert}
            onClose={() => setShowDelayHistory(false)}
          />
        ) : (
          <>
        <div className="rail-heading">
          <div>
            <span>Kitchen flow</span>
            <h1>Section status</h1>
          </div>
          <button
            className="station-reveal-control"
            type="button"
            onClick={advanceDemoEvent}
            disabled={revealedStationCount >= orderedStations.length}
            aria-label="Reveal next station"
          >
            <kbd>N</kbd>
            <span>{revealedStationCount}/{orderedStations.length}</span>
            <Activity size={17} />
          </button>
        </div>
        {stagedDelayAlert ? (
          <div
            className="delay-alert"
            key={`delay-event-${demoEventStep}`}
            role="status"
            aria-live="polite"
            style={delayAlertStyle}
          >
            <div className="delay-alert-heading">
              <span>
                <Database size={13} />
                Similar delay found
              </span>
              <strong>{Math.round(stagedDelayAlert.similarity * 100)}% match</strong>
            </div>
            <p className="delay-alert-primary">
              Expected out in about{" "}
              <strong>{stagedDelayAlert.predictedReadyInMinutes} min</strong>
            </p>
            <p className="delay-alert-context">
              <strong>{stagedDelayAlert.itemLabel}</strong> at{" "}
              {stagedDelayAlert.stationLabel}
              <mark>+{stagedDelayAlert.predictedDelayMinutes} min vs normal</mark>
            </p>
            <small>{stagedDelayAlert.summary}</small>
            <footer>
              <button
                className="delay-evidence-trigger"
                type="button"
                onClick={() => setShowDelayHistory(true)}
                aria-label={`View ${stagedDelayAlert.evidenceCount} historical shifts`}
              >
                {stagedDelayAlert.evidenceCount} historical shifts used
                <ChevronRight size={14} aria-hidden="true" />
              </button>
            </footer>
          </div>
        ) : null}
        <div className="station-list">
          {visibleStations.map((station) => (
            <div className="station-row" key={station.id}>
              <i className={station.status} />
              <div>
                <strong>{station.label}</strong>
                <span>Queue {station.queue} - {formatSeconds(station.etaSeconds)}</span>
              </div>
              <div className="load-track">
                <i style={{ width: `${station.load}%` }} />
              </div>
              <output>{station.load}%</output>
            </div>
          ))}
        </div>
        <div className="aisle-explainer">
          <div className="aisle-title">
            <span>PRIMARY AISLE</span>
            <strong>Clear - 1.2 m minimum</strong>
          </div>
          <p>
            The translucent blue surface is the walkable floor extracted from the kitchen scan.
            Routes stay inside it and avoid equipment footprints. A blocked segment turns red and
            is removed from the next assignment.
          </p>
          <div className="aisle-measures">
            <span><b>1</b> connected route</span>
            <span><b>0</b> obstructions</span>
            <span><b>38.9%</b> walkable floor</span>
          </div>
        </div>
        <div className="prediction-note">
          <Clock3 size={17} />
          <p>
            <strong>Prep before 18:48</strong>
            Vegetable prep constrained the rush in 7 of 8 similar Friday shifts.
          </p>
        </div>
          </>
        )}
      </aside>

      <TaskSheet
        recommendation={recommendation as Recommendation | null}
        worker={taskWorker}
        mode={mode}
        approve={approve}
        pass={pass}
      />
    </main>
  );
}
